import React, { useState, useEffect } from 'react';
import { tenantAdminAPI } from '../../../services/api/tenantAdminAPI';
import toast from 'react-hot-toast';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  UserCheck, 
  Activity, 
  Calendar,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react';

const AnalyticsReports = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await tenantAdminAPI.getAnalyticsSummary();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, change, icon: Icon, color = "vital-blue" }) => {
    const colorClasses = {
      'vital-blue': 'bg-[#DCEAF5] text-[#207CE5]',
      'cyan-core': 'bg-[#DCEAF5] text-[#00BCD4]',
      'teal-signal': 'bg-[#DCEAF5] text-[#008080]',
      'steel-blue': 'bg-[#DCEAF5] text-[#4682B4]'
    };
    
    const bgClass = colorClasses[color] || colorClasses['vital-blue'];
    
    return (
      <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#3D3D3D]">{title}</p>
            <p className="text-2xl font-bold text-[#0B1C2C] mt-2">{value}</p>
            {/* {change && (
              // <p className={`text-sm mt-1 ${change > 0 ? 'text-[#008080]' : 'text-[#00BCD4]'}`}>
              //   {change > 0 ? '+' : ''}{change}% from last period
              // </p>
            )} */}
          </div>
          <div className={`p-3 rounded-lg ${bgClass.split(' ')[0]} flex-shrink-0 ml-4`}>
            <Icon className={`h-6 w-6 ${bgClass.split(' ')[1]}`} />
          </div>
        </div>
      </div>
    );
  };

  const UserRoleChart = () => {
    if (!analytics?.user_counts) return null;

    const roleColors = {
      'tenant_doctor': 'bg-[#207CE5]',
      'tenant_nurse': 'bg-[#008080]',
      'tenant_admin': 'bg-[#3D3D3D]',
      'tenant_technician': 'bg-[#00BCD4]'
    };

    const roleDisplayNames = {
      'tenant_doctor': 'Clinician',
      'tenant_nurse': 'Nurse',
      'tenant_admin': 'Admin',
      'tenant_technician': 'Technician'
    };

    // Filter out patient role, nurse, and technician, and calculate total
    const filteredUserCounts = analytics.user_counts.filter(item => 
      item.role !== 'tenant_patient' && 
      item.role !== 'tenant_nurse' && 
      item.role !== 'tenant_technician'
    );
    const total = filteredUserCounts.reduce((sum, item) => sum + item.count, 0);

    return (
      <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
        <h3 className="text-lg font-semibold text-[#0B1C2C] mb-4">User Distribution by Role</h3>
        
        <div className="space-y-4">
          {filteredUserCounts.map((item) => {
            const percentage = total > 0 ? (item.count / total * 100) : 0;
            const roleName = roleDisplayNames[item.role] || item.role.replace('tenant_', '').charAt(0).toUpperCase() + 
                            item.role.replace('tenant_', '').slice(1);
            
            return (
              <div key={item.role} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded ${roleColors[item.role] || 'bg-[#3D3D3D]'}`}></div>
                  <span className="text-sm font-medium text-[#3D3D3D]">{roleName}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-24 bg-[#DCEAF5] rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${roleColors[item.role] || 'bg-[#3D3D3D]'}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-[#3D3D3D] w-12 text-right">
                    {percentage.toFixed(0)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {filteredUserCounts.length === 0 && (
          <div className="text-center py-8">
            <Users className="h-8 w-8 text-[#3D3D3D] mx-auto mb-2" />
            <p className="text-sm text-[#3D3D3D]">No user data available</p>
          </div>
        )}
      </div>
    );
  };

  const ActivityChart = () => (
    <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
      <h3 className="text-lg font-semibold text-[#0B1C2C] mb-4">Activity Trends</h3>
      
      <div className="h-64 flex items-center justify-center bg-[#DCEAF5] rounded-lg">
        <div className="text-center">
          <BarChart3 className="h-12 w-12 text-[#3D3D3D] mx-auto mb-2" />
          <p className="text-sm text-[#3D3D3D]">Activity chart coming soon</p>
          <p className="text-xs text-[#3D3D3D]">Historical data will be displayed here</p>
        </div>
      </div>
    </div>
  );

  const RecentActivity = () => (
    <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
      <h3 className="text-lg font-semibold text-[#0B1C2C] mb-4">Recent Activity</h3>
      
      <div className="space-y-4">
        {[
          { action: 'New patient registered', time: '2 hours ago', icon: UserCheck, color: 'text-[#008080]' },
          { action: 'User account created', time: '4 hours ago', icon: Users, color: 'text-[#207CE5]' },
          { action: 'Vital signs recorded', time: '6 hours ago', icon: Activity, color: 'text-[#00BCD4]' },
          { action: 'Patient updated profile', time: '8 hours ago', icon: UserCheck, color: 'text-[#4682B4]' },
        ].map((activity, index) => (
          <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-[#DCEAF5]">
            <activity.icon className={`h-5 w-5 ${activity.color}`} />
            <div className="flex-1">
              <p className="text-sm font-medium text-[#0B1C2C]">{activity.action}</p>
              <p className="text-xs text-[#3D3D3D]">{activity.time}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1C2C]">Analytics & Reports</h1>
          <p className="text-[#3D3D3D]">Monitor your organization's performance and activity</p>
        </div>
        <div className="flex space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <button
            onClick={loadAnalytics}
            className="flex items-center space-x-2 px-4 py-2 border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#4682B4]">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Users"
          value={analytics?.user_counts?.reduce((sum, item) => sum + item.count, 0) || 0}
          change={12}
          icon={Users}
          color="vital-blue"
        />
        <MetricCard
          title="Active Patients"
          value={analytics?.patient_count || 0}
          change={8}
          icon={UserCheck}
          color="teal-signal"
        />
        <MetricCard
          title="Vital Records"
          value={analytics?.vitals_count || 0}
          change={15}
          icon={Activity}
          color="cyan-core"
        />
        {/* <MetricCard
          title="This Month"
          value="47"
          change={23}
          icon={Calendar}
          color="steel-blue"
        /> */}
      </div>

      {/* Charts and Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UserRoleChart />
        <ActivityChart />
      </div>

      {/* Recent Activity and Additional Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentActivity />
        
        {/* Performance Insights */}
        <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
          <h3 className="text-lg font-semibold text-[#0B1C2C] mb-4">Performance Insights</h3>
          
          <div className="space-y-4">
            <div className="p-4 bg-[#DCEAF5] border border-[#008080] rounded-lg">
              <div className="flex items-center space-x-3">
                <TrendingUp className="h-5 w-5 text-[#008080]" />
                <div>
                  <p className="text-sm font-medium text-[#0B1C2C]">Growing Patient Base</p>
                  <p className="text-xs text-[#008080]">12% increase in new patient registrations</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-[#DCEAF5] border border-[#207CE5] rounded-lg">
              <div className="flex items-center space-x-3">
                <Activity className="h-5 w-5 text-[#207CE5]" />
                <div>
                  <p className="text-sm font-medium text-[#0B1C2C]">High Engagement</p>
                  <p className="text-xs text-[#207CE5]">Average of 3.2 vital recordings per patient</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-[#DCEAF5] border border-[#00BCD4] rounded-lg">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-[#00BCD4]" />
                <div>
                  <p className="text-sm font-medium text-[#0B1C2C]">Staff Utilization</p>
                  <p className="text-xs text-[#00BCD4]">Optimal staff-to-patient ratio maintained</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Reports Section */}
      <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#0B1C2C]">Detailed Reports</h3>
          <button className="flex items-center space-x-2 px-4 py-2 border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]">
            <Filter className="h-4 w-4" />
            <span>Filter Reports</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-[#DCEAF5] rounded-lg hover:border-[#207CE5] cursor-pointer transition-colors">
            <h4 className="font-medium text-[#0B1C2C] mb-2">User Activity Report</h4>
            <p className="text-sm text-[#3D3D3D] mb-3">Detailed breakdown of user engagement and activity patterns</p>
            <button className="text-sm text-[#207CE5] hover:text-[#4682B4] font-medium">
              Generate Report →
            </button>
          </div>

          <div className="p-4 border border-[#DCEAF5] rounded-lg hover:border-[#207CE5] cursor-pointer transition-colors">
            <h4 className="font-medium text-[#0B1C2C] mb-2">Patient Health Summary</h4>
            <p className="text-sm text-[#3D3D3D] mb-3">Comprehensive overview of patient health metrics and trends</p>
            <button className="text-sm text-[#207CE5] hover:text-[#4682B4] font-medium">
              Generate Report →
            </button>
          </div>

          <div className="p-4 border border-[#DCEAF5] rounded-lg hover:border-[#207CE5] cursor-pointer transition-colors">
            <h4 className="font-medium text-[#0B1C2C] mb-2">Compliance Report</h4>
            <p className="text-sm text-[#3D3D3D] mb-3">HIPAA compliance status and security metrics overview</p>
            <button className="text-sm text-[#207CE5] hover:text-[#4682B4] font-medium">
              Generate Report →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsReports;